import { Component } from "react";
import { AlertTriangle, RefreshCw, Home, ShieldAlert } from "lucide-react";
import { captureException } from "../analytics";
import { T } from "../tokens";

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    try {
      captureException(error, { componentStack: info?.componentStack });
    } catch { /* ignore */ }
    
    if (typeof window !== "undefined" && !window.__FYZ_SILENT_ERRORS) {
      console.error("[Fayriza] Render error:", error, info);
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.assign("/");
  };

  render() {
    if (!this.state.error) return this.props.children;

    const isDev = typeof import.meta !== "undefined" && import.meta.env?.DEV;

    return (
      <div style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "radial-gradient(circle at center, #1A1208 0%, #0D0B08 100%)",
        padding: 24,
        position: "relative",
        overflow: "hidden"
      }}>
        {/* Decorative elements */}
        <div style={{ position: "absolute", top: "10%", left: "10%", width: 300, height: 300, borderRadius: "50%", background: "radial-gradient(circle, rgba(201,168,76,0.05) 0%, transparent 70%)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: "10%", right: "10%", width: 400, height: 400, borderRadius: "50%", background: "radial-gradient(circle, rgba(201,168,76,0.03) 0%, transparent 70%)", pointerEvents: "none" }} />

        <div style={{
          maxWidth: 600,
          width: "100%",
          background: "rgba(26,21,16,0.7)",
          backdropFilter: "blur(24px)",
          border: `1px solid ${T.goldBdr}`,
          borderRadius: 24,
          padding: "48px 40px",
          textAlign: "center",
          position: "relative",
          zIndex: 1,
          boxShadow: "0 40px 100px rgba(0,0,0,0.6)"
        }}>
          <div style={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            background: "rgba(201,168,76,0.1)",
            border: `1px solid ${T.goldBdrS}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 24px",
            color: T.gold
          }}>
            <ShieldAlert size={40} />
          </div>

          <h1 style={{
            fontFamily: "Cormorant Garamond, serif",
            fontSize: 36,
            fontWeight: 600,
            color: T.text,
            marginBottom: 12,
            letterSpacing: "0.01em"
          }}>
            Something went <em style={{ color: T.goldLt, fontStyle: "italic" }}>wrong</em>
          </h1>

          <p style={{
            fontSize: 16,
            color: T.muted,
            lineHeight: 1.6,
            maxWidth: 420,
            margin: "0 auto 32px"
          }}>
            Our systems encountered an unexpected interruption. We've logged the event for our engineering team.
          </p>

          {isDev && (
            <div style={{ marginTop: 32, textAlign: "left" }}>
              <details style={{ cursor: "pointer" }}>
                <summary style={{
                  fontSize: 12,
                  color: T.muted,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  userSelect: "none",
                  marginBottom: 12
                }}>
                  <Info size={14} /> Technical Details
                </summary>
                <div style={{
                  background: "rgba(0,0,0,0.4)",
                  border: `1px solid rgba(201,168,76,0.15)`,
                  borderRadius: 12,
                  padding: 20,
                  maxHeight: 200,
                  overflow: "auto"
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#EF4444", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>
                    <AlertTriangle size={13} /> Error Trace
                  </div>
                  <pre style={{
                    fontSize: 11,
                    fontFamily: "monospace",
                    color: "#BDB5A8",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-all",
                    margin: 0,
                    lineHeight: 1.5
                  }}>
                    {String(this.state.error?.stack || this.state.error?.message || this.state.error)}
                  </pre>
                </div>
              </details>
            </div>
          )}

          <div style={{ display: "flex", gap: 16, justifyContent: "center" }}>
            <button
              onClick={this.handleReload}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "14px 28px",
                borderRadius: 12,
                background: `linear-gradient(135deg, ${T.goldLt}, ${T.gold})`,
                color: T.dark,
                fontWeight: 700,
                fontSize: 15,
                border: "none",
                cursor: "pointer",
                transition: "transform 0.2s"
              }}
              onMouseOver={e => e.currentTarget.style.transform = "scale(1.02)"}
              onMouseOut={e => e.currentTarget.style.transform = "scale(1)"}
            >
              <RefreshCw size={18} /> Retry Page
            </button>
            <button
              onClick={this.handleGoHome}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "14px 28px",
                borderRadius: 12,
                background: "transparent",
                color: T.text,
                fontWeight: 600,
                fontSize: 15,
                border: `1px solid ${T.goldBdr}`,
                cursor: "pointer",
                transition: "all 0.2s"
              }}
              onMouseOver={e => {
                e.currentTarget.style.background = "rgba(201,168,76,0.05)";
                e.currentTarget.style.borderColor = T.gold;
              }}
              onMouseOut={e => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.borderColor = T.goldBdr;
              }}
            >
              <Home size={18} /> Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }
}
